import { useEffect, useState } from "react";
import { ArrowUpRight, Mail } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { getSubstackPosts } from "@/lib/api";

export default function SubstackSection() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    getSubstackPosts()
      .then((r) => setPosts(r.data.posts || []))
      .catch(() => setPosts([]));
  }, []);

  return (
    <section className="bg-cream border-y border-line py-20 lg:py-28" data-testid="substack-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <Eyebrow className="mb-6">From the Substack</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">Latest notes from Jen</h2>
          </div>
          <a
            href="https://substack.com/@greenliteracy"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-terra link-underline shrink-0"
            data-testid="substack-follow-link"
          >
            Follow @greenliteracy <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </Reveal>

        {posts === null ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="substack-loading">
            {[0, 1].map((i) => (
              <div key={i} className="border border-line rounded-lg bg-paper p-8 h-36 animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <Reveal>
            <a
              href="https://substack.com/@greenliteracy"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-5 border border-line rounded-lg bg-paper p-8 hover:bg-goldsoft/40 transition-colors duration-300"
              data-testid="substack-fallback-card"
            >
              <span className="w-11 h-11 rounded-full bg-goldsoft text-terra flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5" strokeWidth={1.5} />
              </span>
              <span>
                <span className="block font-serif text-xl text-ink">The Green Literacy Substack</span>
                <span className="block text-txtmut text-sm mt-1">
                  Essays, reading lists, and field notes on literacy and the living world — read and follow on Substack.
                </span>
              </span>
              <ArrowUpRight className="w-5 h-5 text-txtmut/40 group-hover:text-terra ml-auto shrink-0" />
            </a>
          </Reveal>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {posts.map((p, i) => (
              <Reveal key={p.url} delay={i * 0.08}>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col h-full border border-line rounded-lg bg-paper hover:bg-goldsoft/40 p-8 transition-colors duration-300"
                  data-testid={`substack-post-${i + 1}`}
                >
                  {p.date && <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-txtmut mb-3">{p.date}</p>}
                  <p className="font-serif text-xl text-ink leading-snug group-hover:text-terra transition-colors duration-300">{p.title}</p>
                  <span className="mt-auto pt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-terra">
                    Read on Substack <ArrowUpRight className="w-3.5 h-3.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
